import { PRODUCT_IMAGE_POLICY as policy, productImageDimensions } from '@travel/contracts';

export async function optimizeProductImage(file: File): Promise<File> {
  if (
    !['image/jpeg', 'image/png'].includes(file.type) ||
    file.size > policy.maxSourceBytes ||
    !file.size
  ) {
    throw new Error('请选择 10MB 内的 JPG / PNG 图片');
  }
  if (typeof createImageBitmap !== 'function') {
    throw new Error('浏览器不支持图片优化，请使用最新版 Chrome 或 Edge 上传');
  }
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => {
    throw new Error('图片无法解析，请重新选择有效的 JPG / PNG 图片');
  });
  const canvas = document.createElement('canvas');
  try {
    const size = productImageDimensions(bitmap.width, bitmap.height);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d', { willReadFrequently: file.type === 'image/png' });
    if (!context) throw new Error('无法处理图片，请更换浏览器后重试');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, size.width, size.height);
    let transparent = false;
    if (file.type === 'image/png') {
      const pixels = context.getImageData(0, 0, size.width, size.height).data;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i]! < 255) {
          transparent = true;
          break;
        }
      }
    }
    const type = transparent ? 'image/png' : 'image/jpeg';
    const encode = (quality: number) =>
      new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('图片压缩失败，请重新选择图片'))),
          type,
          quality,
        ),
      );
    let blob = await encode(policy.quality);
    if (!transparent && blob.size > policy.targetBytes) {
      const smaller = await encode(policy.minimumQuality);
      if (smaller.size < blob.size) blob = smaller;
    }
    // Small, already mobile-sized images should not grow or be needlessly recompressed.
    if (
      size.width === bitmap.width &&
      size.height === bitmap.height &&
      file.size <= policy.targetBytes &&
      blob.size >= file.size
    )
      return file;
    if (blob.size > policy.maxBytes)
      throw new Error('图片细节过多，优化后仍超过 1.5MB，请拆分长图或选择较小图片，避免损失清晰度');
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.${transparent ? 'png' : 'jpg'}`, {
      type,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
}

export async function prepareProductImageUpload(path: string, form: FormData): Promise<FormData> {
  if (!/^\/api\/admin\/m2\/apps\/[^/]+\/products\/[^/]+\/media\/upload$/.test(path)) return form;
  const file = form.get('file');
  if (!(file instanceof File)) throw new Error('请选择产品图片');
  const optimized = await optimizeProductImage(file);
  const prepared = new FormData();
  form.forEach((value, key) => {
    if (key !== 'file') prepared.append(key, value);
  });
  prepared.set('file', optimized);
  return prepared;
}
